import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';

import BoardContext from 'contexts/BoardContext';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import { DriverBonusOverviewContainer } from 'components/DriverAX/screens/DriverBonusOverview';
import AdditionalPaymentsPanel from 'components/DriverAX/components/AdditionalPayments/AdditionalPaymentsPanel';
import AdditionalPaymentsModeProvider from 'components/DriverAX/components/AdditionalPayments/AdditionalPaymentsModeProvider';
import SlideOutContext from '../contexts/PerformanceBoardSlideOutContext';
import isPerformanceBoard from 'isPerformanceBoard';
import SlideOutHeader from './SlideOutHeader';
import EmptySlideout from './EmptySlideout/EmptySlideout';
import SlideOut from './SlideOut';
import HomeList from './HomeWidgetList/HomeList';
import WizardSlideout from './WizardSlideout';
import OverrideEntityChangeGuard from './OverrideEntityChangeGuard';
import BoardBonusOverviewProvider from '../../DriverAX/providers/BoardBonusOverviewProvider';
import useWeeklyFactTableOverride from '../hooks/useWeeklyFactTableOverride';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
`;

// Mirrors DriverBonusProgram's 'standalone' wrapper so the reused override body
// fills the fixed-height slideout: the rewards list scrolls internally and the
// total bar stays pinned at the bottom.
const OverrideBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const PerformanceBoardSlideOutContainer = () => {
  const { navState, setNavState, board, isOpen } = useContext(SlideOutContext);
  const { currentMode, exitOverrideMode, exitAdditionalPaymentsMode } =
    useContext(BonusOverviewContext);
  const { isWeeklyOverrideBoard } = useWeeklyFactTableOverride();

  useEffect(() => {
    if (board.slideout.length === 0) {
      setNavState({ slideoutMode: 'Empty' });
    }
  }, [board, setNavState]);

  // Reset override mode whenever the slideout is closed so it doesn't reopen
  // mid-override (e.g. after closing then selecting another row).
  useEffect(() => {
    if (!isOpen && currentMode.mode === 'override') {
      exitOverrideMode();
    }
    if (!isOpen && currentMode.mode === 'additionalPayments') {
      exitAdditionalPaymentsMode();
    }
  }, [isOpen, currentMode.mode, exitOverrideMode, exitAdditionalPaymentsMode]);

  // Recognition payments mode likewise transforms the slideout in place: the
  // header shows the mode's title + Back, the body the add form or the list.
  if (currentMode.mode === 'additionalPayments') {
    return (
      <AdditionalPaymentsModeProvider>
        <Content>
          <SlideOutHeader slideoutMode={'Home'} />
          <OverrideBody>
            <AdditionalPaymentsPanel />
          </OverrideBody>
        </Content>
      </AdditionalPaymentsModeProvider>
    );
  }

  // Override mode transforms the slideout in place into the full bonus override
  // workflow (reused from the entity-app screen). The header switches to the
  // override actions (see SlideOutHeader), the body to the rewards + totals.
  //
  // Sub-period boards with the fact-table-override setting on instead keep the
  // normal sections (their per-week fact tables render payout dropdowns in
  // override mode), so the weekly breakdown is the override surface.
  if (currentMode.mode === 'override') {
    return (
      <Content>
        <OverrideEntityChangeGuard />
        <SlideOutHeader slideoutMode={'Home'} />
        <OverrideBody>
          {isWeeklyOverrideBoard ? (
            <HomeList sections={board.slideout} />
          ) : (
            <DriverBonusOverviewContainer layout={'standalone'} />
          )}
        </OverrideBody>
      </Content>
    );
  }

  return (
    <Content>
      {navState.slideoutMode === 'Empty' && (
        <>
          <SlideOutHeader slideoutMode={'Empty'} />
          <EmptySlideout />
        </>
      )}
      {navState.slideoutMode === 'Home' && (
        <>
          <SlideOutHeader slideoutMode={'Home'} />
          <HomeList sections={board.slideout} />
        </>
      )}
      {navState.slideoutMode === 'Creating' && <WizardSlideout />}
    </Content>
  );
};

const Gate = () => {
  const { board } = useContext(BoardContext);

  if (!isPerformanceBoard(board)) {
    return null;
  }

  return (
    <SlideOut>
      <BoardBonusOverviewProvider board={board}>
        <PerformanceBoardSlideOutContainer />
      </BoardBonusOverviewProvider>
    </SlideOut>
  );
};

export default Gate;
